/**
 * trustedHtml — narrow boundary for audited library-generated markup.
 *
 * React escaping should be the default. The few demo-ui surfaces that
 * need HTML strings receive them only from trusted renderers: KaTeX for
 * math and Shiki for syntax highlighting. This module brands those
 * strings after a small fail-closed check so every
 * `dangerouslySetInnerHTML` callsite is easy to grep and review.
 *
 * The check is implemented as a single linear-time scan rather than a
 * stack of regex tests. The previous regex-based implementation had
 * two latent gaps — long whitespace runs between `<` and a tag name,
 * and `<svg/onload=...>`-style slash-separated attributes — that an
 * adversarial renderer output could have used to bypass the boundary.
 * A character-by-character lexer closes both gaps without any
 * quantifier ambiguity, so the scanner is strictly O(n) and cannot
 * regress to polynomial time on adversarial input.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

const trustedHtmlBrand: unique symbol = Symbol("TrustedHtml");

export type TrustedHtmlSource = "katex" | "shiki";

export interface TrustedHtmlValue {
  readonly __html: string;
  readonly source: TrustedHtmlSource;
  readonly [trustedHtmlBrand]: true;
}

export class TrustedHtmlError extends Error {
  constructor(
    public readonly source: TrustedHtmlSource,
    message: string,
  ) {
    super(message);
    this.name = "TrustedHtmlError";
  }
}

const ALLOWED_RENDERER_TAGS = new Set(["br", "code", "div", "pre", "span"]);
const URL_BEARING_ATTRIBUTES = new Set(["href", "src", "xlink:href"]);
const SCRIPT_URL_PREFIXES = ["javascript:", "vbscript:", "data:text/html"];

// ── Character-class predicates (inlined to keep the scanner branch-free) ─

function isHtmlWhitespace(code: number): boolean {
  // Per the HTML spec ASCII whitespace set: SP / TAB / LF / FF / CR.
  return code === 0x20 || code === 0x09 || code === 0x0a || code === 0x0c || code === 0x0d;
}

function isTagNameChar(code: number): boolean {
  // Letters, digits, ASCII colon (XML namespaces), underscore, hyphen.
  return (
    (code >= 0x30 && code <= 0x39) ||
    (code >= 0x41 && code <= 0x5a) ||
    (code >= 0x61 && code <= 0x7a) ||
    code === 0x3a ||
    code === 0x5f ||
    code === 0x2d
  );
}

function isAttributeNameChar(code: number): boolean {
  // Stop at whitespace, `=`, `/`, `>`. Everything else is treated as
  // part of the attribute name so malformed names still get classified
  // and the surrounding tag still reaches the allowlist check.
  return (
    !isHtmlWhitespace(code) &&
    code !== 0x3d /* = */ &&
    code !== 0x2f /* / */ &&
    code !== 0x3e /* > */
  );
}

// ── Main entry point ─────────────────────────────────────────────────────

function assertTrustedHtml(html: string, source: TrustedHtmlSource): void {
  const n = html.length;
  let i = 0;
  while (i < n) {
    if (html.charCodeAt(i) === 0x3c /* '<' */) {
      i = scanTagAndAttributes(html, i, source);
    } else {
      i += 1;
    }
  }
}

/**
 * Scan a single `<...>` tag starting at `start` (which must point at
 * `<`). Returns the offset one past the closing `>` on success.
 * Throws `TrustedHtmlError` if the tag is malformed, the tag name is
 * outside the allowlist, an attribute is an inline event handler, or
 * an attribute value carries a script-bearing URL or CSS construct.
 */
function scanTagAndAttributes(html: string, start: number, source: TrustedHtmlSource): number {
  const n = html.length;
  let i = start + 1;

  // Skip whitespace, then an optional closing-tag `/`, then more
  // whitespace. The arbitrary-whitespace case (e.g. `<       script>`)
  // is intentionally handled here so it reaches the tag-name check
  // rather than slipping past a bounded regex.
  while (i < n && isHtmlWhitespace(html.charCodeAt(i))) i += 1;
  if (i < n && html.charCodeAt(i) === 0x2f /* '/' */) {
    i += 1;
    while (i < n && isHtmlWhitespace(html.charCodeAt(i))) i += 1;
  }

  const nameStart = i;
  while (i < n && isTagNameChar(html.charCodeAt(i))) i += 1;
  if (i === nameStart) {
    throw new TrustedHtmlError(source, "library HTML contained a malformed tag");
  }
  const tagName = html.slice(nameStart, i).toLowerCase();
  if (!ALLOWED_RENDERER_TAGS.has(tagName)) {
    throw new TrustedHtmlError(source, `library HTML contained a disallowed tag: ${tagName}`);
  }

  // Attribute loop. Both whitespace and `/` are treated as attribute
  // separators (HTML allows `<svg/onload=…>`); the original
  // regex-based check missed the slash case.
  while (i < n) {
    let sawSeparator = false;
    while (i < n) {
      const c = html.charCodeAt(i);
      if (isHtmlWhitespace(c) || c === 0x2f /* '/' */) {
        i += 1;
        sawSeparator = true;
      } else {
        break;
      }
    }
    if (i >= n) break;
    const c = html.charCodeAt(i);
    if (c === 0x3e /* '>' */) return i + 1;
    if (!sawSeparator) {
      throw new TrustedHtmlError(source, "library HTML contained a malformed tag");
    }

    const attrStart = i;
    while (i < n && isAttributeNameChar(html.charCodeAt(i))) i += 1;
    if (i === attrStart) {
      throw new TrustedHtmlError(source, "library HTML contained a malformed attribute");
    }
    const attrName = html.slice(attrStart, i).toLowerCase();
    rejectEventHandlerAttribute(attrName, source);

    while (i < n && isHtmlWhitespace(html.charCodeAt(i))) i += 1;

    let attrValue = "";
    if (i < n && html.charCodeAt(i) === 0x3d /* '=' */) {
      i += 1;
      while (i < n && isHtmlWhitespace(html.charCodeAt(i))) i += 1;
      if (i < n) {
        const quote = html.charCodeAt(i);
        if (quote === 0x22 /* '"' */ || quote === 0x27 /* "'" */) {
          i += 1;
          const valueStart = i;
          while (i < n && html.charCodeAt(i) !== quote) i += 1;
          attrValue = html.slice(valueStart, i);
          if (i < n) i += 1;
        } else {
          const valueStart = i;
          while (i < n) {
            const cc = html.charCodeAt(i);
            if (cc === 0x3e /* '>' */ || isHtmlWhitespace(cc)) break;
            i += 1;
          }
          attrValue = html.slice(valueStart, i);
        }
      }
    }

    if (URL_BEARING_ATTRIBUTES.has(attrName)) {
      rejectScriptUrl(attrValue, source);
    }
    if (attrName === "style") {
      rejectScriptCss(attrValue, source);
    }
  }

  throw new TrustedHtmlError(source, "library HTML contained an unclosed tag");
}

function rejectEventHandlerAttribute(attrName: string, source: TrustedHtmlSource): void {
  if (
    attrName.length < 3 ||
    attrName.charCodeAt(0) !== 0x6f /* 'o' */ ||
    attrName.charCodeAt(1) !== 0x6e /* 'n' */
  ) {
    return;
  }
  for (let j = 2; j < attrName.length; j += 1) {
    const c = attrName.charCodeAt(j);
    if (c < 0x61 || c > 0x7a /* a-z (lowered above) */) return;
  }
  throw new TrustedHtmlError(source, "library HTML contained an inline event handler");
}

function rejectScriptUrl(value: string, source: TrustedHtmlSource): void {
  const trimmed = trimAsciiWhitespace(value).toLowerCase();
  for (const prefix of SCRIPT_URL_PREFIXES) {
    if (trimmed.startsWith(prefix)) {
      throw new TrustedHtmlError(source, "library HTML contained a script-bearing URL");
    }
  }
}

function rejectScriptCss(value: string, source: TrustedHtmlSource): void {
  const lower = value.toLowerCase();
  const n = lower.length;
  let i = 0;
  while (i < n) {
    const c = lower.charCodeAt(i);
    if (c === 0x75 /* 'u' */ && lower.startsWith("url(", i)) {
      let j = i + 4;
      while (j < n && isHtmlWhitespace(lower.charCodeAt(j))) j += 1;
      if (j < n && (lower.charCodeAt(j) === 0x22 || lower.charCodeAt(j) === 0x27)) j += 1;
      while (j < n && isHtmlWhitespace(lower.charCodeAt(j))) j += 1;
      for (const prefix of SCRIPT_URL_PREFIXES) {
        if (lower.startsWith(prefix, j)) {
          throw new TrustedHtmlError(source, "library HTML contained a script-bearing CSS URL");
        }
      }
      i += 4;
      continue;
    }
    if (c === 0x65 /* 'e' */ && lower.startsWith("expression", i)) {
      let j = i + 10;
      while (j < n && isHtmlWhitespace(lower.charCodeAt(j))) j += 1;
      if (j < n && lower.charCodeAt(j) === 0x28 /* '(' */) {
        throw new TrustedHtmlError(source, "library HTML contained a CSS expression");
      }
      i += 10;
      continue;
    }
    i += 1;
  }
}

function trimAsciiWhitespace(value: string): string {
  let start = 0;
  let end = value.length;
  while (start < end && isHtmlWhitespace(value.charCodeAt(start))) start += 1;
  while (end > start && isHtmlWhitespace(value.charCodeAt(end - 1))) end -= 1;
  return start === 0 && end === value.length ? value : value.slice(start, end);
}

export function createTrustedHtml(html: string, source: TrustedHtmlSource): TrustedHtmlValue {
  assertTrustedHtml(html, source);
  return Object.freeze({
    __html: html,
    source,
    [trustedHtmlBrand]: true as const,
  });
}
